import {Box, Collapse, Tooltip, Typography} from '@mui/material';
import {CallMade, CallReceived, ExpandMore} from '@mui/icons-material';
import dayjs from 'dayjs';
import {FC, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {OcppLogDTO} from 'src/types';
import {
    getLogTypeColors,
    humanizeOcppType,
    parseOcppPayload,
    prettyPrintPayload,
} from 'src/utils/ocppLogs';
import {
    actionGroupSx,
    actionTextSx,
    cardSx,
    cardWrapperSx,
    codeBlockSx,
    dateSx,
    directionIconBoxSx,
    dividerSx,
    dotSx,
    expandedPanelSx,
    expandIconSx,
    innerDividerSx,
    logIdLabelSx,
    logIdSx,
    logIdValueSx,
    metaItemSx,
    metaLabelSx,
    metaStripSx,
    metaValueSx,
    timeColSx,
    timeSx,
    typeColSx,
} from './OcppLogCard.sx';

interface OcppLogCardProps {
    log: OcppLogDTO;
}

export const OcppLogCard: FC<OcppLogCardProps> = ({log}) => {
    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);

    const colors = getLogTypeColors(log.ocppMessageType);
    const parsed = parseOcppPayload(log.payload);
    const isOut = log.logsType === 'CHARGER_OUT';

    const actionTitle = humanizeOcppType(parsed.action, log.ocppMessageType);
    const directionTitle = intl.formatMessage({
        id: isOut ? 'ocppLogsDirOut' : 'ocppLogsDirIn',
    });

    const time = dayjs(log.occurredAt).format('HH:mm:ss.SSS');
    const date = dayjs(log.occurredAt).format('DD MMM YYYY');

    const toggle = () => setExpanded((prev) => !prev);

    return (
        <Box sx={cardWrapperSx}>
            <Box sx={cardSx(expanded)} onClick={toggle}>
                <Tooltip title={directionTitle}>
                    <Box sx={directionIconBoxSx(isOut ? 'out' : 'in')}>
                        {isOut ? <CallReceived/> : <CallMade/>}
                    </Box>
                </Tooltip>
                <Box sx={timeColSx}>
                    <Typography sx={timeSx}>{time}</Typography>
                    <Typography sx={dateSx}>{date}</Typography>
                </Box>
                <Box sx={dividerSx}/>
                <Box sx={typeColSx}>
                    <Box sx={actionGroupSx}>
                        <Tooltip title={log.ocppMessageType}>
                            <Box sx={dotSx(colors.text)}/>
                        </Tooltip>
                        <Typography sx={actionTextSx}>{actionTitle}</Typography>
                    </Box>
                    <Box sx={innerDividerSx}/>
                    <Box component="span" sx={logIdSx}>
                        <Box component="span" sx={logIdLabelSx}>
                            <FormattedMessage id="ocppLogsLogId"/>
                        </Box>
                        <Box component="span" sx={logIdValueSx}>{log.id}</Box>
                    </Box>
                    {parsed.uniqueId && (
                        <>
                            <Box sx={innerDividerSx}/>
                            <Box component="span" sx={logIdSx}>
                                <Box component="span" sx={logIdLabelSx}>
                                    <FormattedMessage id="ocppLogsUniqueOcppId"/>
                                </Box>
                                <Box component="span" sx={logIdValueSx}>{parsed.uniqueId}</Box>
                            </Box>
                        </>
                    )}
                </Box>
                <ExpandMore sx={expandIconSx(expanded)}/>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={expandedPanelSx}>
                    <Box sx={metaStripSx}>
                        <Box sx={metaItemSx}>
                            <Box component="span" sx={metaLabelSx}>
                                <FormattedMessage id="ocppLogsColDirection"/>
                            </Box>
                            <Box component="span" sx={metaValueSx}>{directionTitle}</Box>
                        </Box>
                        {parsed.action && (
                            <Box sx={metaItemSx}>
                                <Box component="span" sx={metaLabelSx}>
                                    <FormattedMessage id="ocppLogsAction"/>
                                </Box>
                                <Box component="span" sx={metaValueSx}>{parsed.action}</Box>
                            </Box>
                        )}
                        {parsed.messageTypeId !== undefined && (
                            <Box sx={metaItemSx}>
                                <Box component="span" sx={metaLabelSx}>
                                    <FormattedMessage id="ocppLogsMessageTypeId"/>
                                </Box>
                                <Box component="span" sx={metaValueSx}>{parsed.messageTypeId}</Box>
                            </Box>
                        )}
                    </Box>
                    <Box component="pre" sx={codeBlockSx}>
                        {prettyPrintPayload(log.payload)}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};
